import React, { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { filterBy } from '@progress/kendo-data-query';
import {
    MultiSelect as KendoMultiSelect,
    MultiSelectChangeEvent,
    MultiSelectFilterChangeEvent,
    MultiSelectHandle,
    MultiSelectProps as KendoMultiSelectProps,
    TagData,
} from '@progress/kendo-react-dropdowns';
import { Label } from '@progress/kendo-react-labels';
import cn from 'clsx';
import { Country, getCodes } from 'country-list';
import ChipList from '../chip-list';
import { ExcludeArray } from '@components-clean/core/drop-down-list';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import ValidationIconSuffix from '@components-clean/core/icons/validation-icon-suffix';
import { useTranslation } from '@hooks/index';

import './index.scss';

export interface MultiSelectProps<T> extends Omit<KendoMultiSelectProps, 'label'> {
    'data-testid': string;
    data: ExcludeArray<T>[];
    value?: ExcludeArray<T>[];
    suffix?: KendoMultiSelectProps['suffix'];
    inline?: boolean;
    dataItemKey?: Extract<keyof T, string>;
    maxLines?: 1 | 2;
    label?: string | ReactElement;
    isEditing?: boolean;
    useDefaultCustomValueHandler?: boolean;
    activeField?: string;
    isCountryMultiSelect?: boolean;
}

interface MultiSelectControllerProps<TData, TControl extends FieldValues> extends MultiSelectProps<TData> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean>;
    handleInputChange?: (event: MultiSelectChangeEvent) => void;
}

const shouldShowGroupHeader = <T,>(
    groupField: string | undefined,
    groupValue: string,
    remainingItems: ExcludeArray<T>[],
) => {
    return groupField
        ? remainingItems.some((item) => (item as Record<string, unknown>)[groupField] === groupValue)
        : false;
};

const groupHeaderItemRender = <T,>(
    groupField: string | undefined,
    li: ReactElement<HTMLLIElement>,
    remainingItems: ExcludeArray<T>[],
) => {
    const child = React.isValidElement(li.props.children) ? li.props.children : null;
    const groupValue = React.isValidElement(child)
        ? ((child as React.ReactElement<HTMLSpanElement>).props.children as unknown as string)
        : undefined;

    if (!groupValue || !shouldShowGroupHeader(groupField, groupValue, remainingItems)) return null;

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-clone-element
    return React.cloneElement(
        li,
        { ...li.props, className: cn(li.props.className, 'headerItemElement') },
        <span className="headerItem">{groupValue}</span>,
    );
};

const groupStickyHeaderItemRender = <T,>(
    groupField: string | undefined,
    div: ReactElement<HTMLDivElement>,
    remainingItems: ExcludeArray<T>[],
) => {
    const child = React.isValidElement(div.props.children) ? div.props.children : null;
    const groupValue = React.isValidElement(child)
        ? ((child as React.ReactElement<HTMLSpanElement>).props.children as unknown as string)
        : undefined;

    if (!groupValue || !shouldShowGroupHeader(groupField, groupValue, remainingItems)) return null;

    const item = <div className={cn('headerItem sticky')}>{groupValue}</div>;
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-clone-element
    return React.cloneElement(div, { ...div.props, className: cn(div.props.className, 'headerItemElement') }, item);
};

const MultiSelect = <T,>({
    placeholder,
    value,
    validationMessage,
    valid,
    suffix,
    'data-testid': testId,
    data = [],
    allowCustom = true,
    label,
    inline,
    filterable = true,
    dataItemKey,
    onChange,
    maxLines = 2,
    isEditing = false,
    useDefaultCustomValueHandler = true,
    activeField,
    isCountryMultiSelect = false,
    ...rest
}: MultiSelectProps<T>) => {
    const ref = useRef<MultiSelectHandle>(null);

    const { t } = useTranslation();
    const countryCodes = useMemo(() => getCodes(), []);

    const hasInactiveValues = () => {
        if (isCountryMultiSelect)
            return (
                value &&
                Array.isArray(value) &&
                value.some((country) => !countryCodes.includes((country as Country).code))
            );
        return (
            value &&
            Array.isArray(value) &&
            activeField &&
            value.some(
                (inputValue: ExcludeArray<T>) =>
                    inputValue &&
                    typeof inputValue === 'object' &&
                    Object.hasOwn(inputValue as Record<string, unknown>, activeField) &&
                    !Boolean((inputValue as Record<string, unknown>)[activeField]),
            )
        );
    };

    const [datasource, setDatasource] = useState<T[]>([]);

    const [prevValueArrayLength, setPrevValueArrayLength] = useState<number>();

    const renderSuffix = (): ReactElement => {
        return (
            <div className="k-display-flex k-align-items-center">
                {valid === false && <ValidationIconSuffix validationMessage={validationMessage} />}
                {!!suffix && (typeof suffix === 'function' ? React.createElement(suffix) : suffix)}
            </div>
        );
    };

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    // scroll to the bottom of the input - but only when adding items
    useEffect(() => {
        const el = ref.current?.element?.querySelector('.k-input-values');
        if (el && value) {
            // don't scroll to bottom of the input when removing items
            if (prevValueArrayLength && value?.length > prevValueArrayLength) {
                el.scrollTop = el.scrollHeight;
            }

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setPrevValueArrayLength(value.length);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [value]);

    const compare = (first: ExcludeArray<T>, snd: ExcludeArray<T>) => {
        return (
            dataItemKey &&
            first &&
            snd &&
            Object.hasOwn(first, dataItemKey) &&
            Object.hasOwn(snd, dataItemKey) &&
            first[dataItemKey] === snd[dataItemKey]
        );
    };

    const filterDatasource = () => {
        return value
            ? [...data].filter((dataItem) => {
                  return (
                      value.findIndex((selItem) => {
                          return compare(selItem, dataItem) || selItem === dataItem;
                      }) < 0
                  );
              })
            : [...data];
    };

    const onFilterChange = (event: MultiSelectFilterChangeEvent) => {
        const newDataSource = filterDatasource();
        setDatasource(filterBy(newDataSource, event.filter));
    };

    useEffect(() => {
        const newDataSource = filterDatasource();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasource(newDataSource);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data, value]);

    const isCustom = (item: ExcludeArray<T>) => {
        return item && dataItemKey && !Object.hasOwn(item, dataItemKey);
    };
    const addKey = (item: T) => {
        item && dataItemKey && Object.assign(item, { ...item, [dataItemKey]: new Date().getTime() });
    };

    const handleChange = (event: MultiSelectChangeEvent) => {
        const values = event.target.value as ExcludeArray<T>[];
        const lastItem = values[values.length - 1];

        if (useDefaultCustomValueHandler && lastItem && isCustom(lastItem)) {
            values.pop();
            const sameItem = values.find((v) => compare(v, lastItem));
            if (sameItem === undefined) {
                addKey(lastItem);
                values.push(lastItem);
            }
        }

        onChange && onChange({ ...event, value: values });
    };

    const componentClass = cn({ 'one-line': maxLines === 1, 'opra-inactive-value': hasInactiveValues() });

    const getActiveValue = (tagData: TagData) => {
        if (isCountryMultiSelect) {
            return countryCodes.includes(tagData.data?.[0]?.code);
        }

        if (activeField) {
            const dataItem = data.find(
                (item) =>
                    typeof item === 'object' &&
                    item !== null &&
                    tagData.data?.[0]?.id != null &&
                    'id' in item &&
                    item.id === tagData.data[0].id,
            );
            if (dataItem && typeof dataItem === 'object' && Object.hasOwn(dataItem, activeField)) {
                return Boolean((dataItem as Record<string, unknown>)[activeField]);
            }
        }
        return true;
    };

    const tagRender = (tagData: TagData, tag: React.ReactElement<Record<string, ReactNode>>) => {
        const activeValue = getActiveValue(tagData);
        if (!activeValue) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/no-clone-element
            return React.cloneElement(tag, { ...tag.props, className: 'inactiveMultiSelectTag' }, [
                (
                    <span className="inactiveMultiSelectTagContent" key={tag.key}>
                        <InactiveIcon dataTestId={testId} tooltipPosition="top" isSuffix padding="none" />
                        {tagData.text}
                    </span>
                ) as ReactNode,
                tag.props.children,
            ]);
        }
        return tag;
    };

    return (
        <div
            className={cn('multi-select', 'k-w-full', {
                'k-flex-grow': !rest.style?.width,
                'k-d-flex k-gap-4 k-align-items-center': inline,
            })}
        >
            <Label
                editorRef={ref}
                className={cn('k-d-flex k-align-items-end', {
                    '!k-pb-0 k-white-space-nowrap': inline,
                })}
            >
                {label}
            </Label>
            {isEditing ? (
                <KendoMultiSelect
                    {...rest}
                    onChange={handleChange}
                    dataItemKey={dataItemKey}
                    tagRender={tagRender}
                    value={value}
                    data={datasource}
                    onFilterChange={filterable ? rest.onFilterChange || onFilterChange : undefined}
                    placeholder={placeholder ?? t('cm2EnterValue')}
                    suffix={renderSuffix}
                    allowCustom={allowCustom}
                    filterable={filterable}
                    // NOTE: We don't want the "No data found" message to be shown when there is no data is provided
                    listNoDataRender={!data.length ? () => '' : undefined}
                    className={componentClass}
                    ref={ref}
                    summarizeAfter={Number.MAX_VALUE}
                />
            ) : (
                <ChipList
                    data-testid="chip-list"
                    disabled
                    className="read-only-chip-list"
                    data={value?.map((item) => ({ text: item, value: item }))}
                />
            )}
        </div>
    );
};

const MultiSelectController = <TData, TControl extends FieldValues>({
    control,
    name,
    data,
    disabled = false,
    'data-testid': dataTestId,
    label,
    textField,
    required = false,
    validateRules,
    groupField,
    fillMode = 'solid',
    handleInputChange,
    autoClose = false,
    maxLines = 2,
    ...props
}: MultiSelectControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                const remainingItems = data.filter(
                    (dataItem) =>
                        !value?.some(
                            (selectedItem: ExcludeArray<TData>) =>
                                selectedItem[props.dataItemKey!] === dataItem[props.dataItemKey!],
                        ),
                );

                return (
                    <MultiSelect
                        {...props}
                        value={value}
                        onChange={(event) => {
                            onChange(event.value);
                            if (handleInputChange) handleInputChange(event);
                        }}
                        data={data}
                        data-testid={dataTestId}
                        label={label}
                        disabled={disabled}
                        textField={textField}
                        valid={!error}
                        validationMessage={error?.message}
                        groupField={groupField}
                        fillMode={fillMode}
                        autoClose={autoClose}
                        maxLines={maxLines}
                        groupHeaderItemRender={(li) => groupHeaderItemRender(groupField, li, remainingItems)}
                        groupStickyHeaderItemRender={(div) =>
                            groupStickyHeaderItemRender(groupField, div, remainingItems)
                        }
                    />
                );
            }}
        />
    );
};

export { MultiSelect, MultiSelectController };
